import { useEffect, useState, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { startCase } from 'lodash';
import { useDispatch } from 'react-redux';
import { redirect } from 'redux-first-router';
import { formatDistanceToNow, differenceInMinutes } from 'date-fns';
import { formatDaysUntilEnd } from '../../../../utils/formatDaysUntilEnd';
import { getCurrencySymbol } from './utils';

import { toTruncated } from '@/utilities/formatting';
import TickSolid from '@simplywallst/ui-core/icons/TickSolid';

import { useSnackbar } from '@/features/snackbar';

import If from '@/components/If';

import type { CouponState } from '@/hooks/useUser';
import useUser from '@/hooks/useUser';
import { useUserPlans } from '@/hooks/useUserPlans';
import { useRewardfulData } from '@/features/rewardful';
import { useCoupon } from '@/hooks/useCoupon';

import { ROUTE_DASHBOARD } from '@/constants/routes';

import { PlansSelection } from './components/PlansSelection';
import {
  FeatureItem,
  MarkedText,
  AddedFeatures,
  MarkedTextContainer,
} from './styled';
import { BillingPeriod, defaultPlan, defaultPlansPrice } from './constants';
import {
  getCoupon,
  mutateUserCoupon,
  deleteCoupon,
  defaultCoupon,
} from './query';
import type { Plan, PlansPrices } from './interface';
import { useSubscriptionRecovery } from '@/hooks/useSubscriptionRecovery';
import { Text, Button, Box } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
export const formatMonthlyPrice = (
  amount: number,
  billingPeriod: BillingPeriod,
  currencyISO: string
): string => {
  const monthlyPrice = amount / (billingPeriod === 'year' ? 12 : 1);
  const currencySymbol = getCurrencySymbol(currencyISO);
  return `${currencySymbol}${toTruncated(
    monthlyPrice,
    monthlyPrice % 1 === 0 ? 0 : 2
  )}`;
};

export interface PlansProps {
  darkBackground?: boolean;
  hideUserMessage?: boolean;
  hideFreePlan?: boolean;
  onClose?: () => void;
  onAction?: () => void;
}

export const Plans = ({
  darkBackground,
  hideUserMessage,
  hideFreePlan,
  onClose,
  onAction,
}: PlansProps) => {
  const [userPlan, setUserPlan] = useState<Plan>(defaultPlan);
  const [planPrices, setPlanPrices] = useState<PlansPrices>(defaultPlansPrice);
  const [showModal] = useState(false);
  const [urlCoupon, setUrlCoupon] = useState<string | null>(null);
  const { addSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  useCoupon();

  const { data: userData, refetch: refetchUser } = useUser();
  const {
    billing: { trialEnd },
  } = userData;
  const { status, plans } = useUserPlans();
  const { mutate: updateUserCoupon } = useMutation({
    mutationFn: mutateUserCoupon,
  });
  const { mutate: deleteUserCoupon } = useMutation({
    mutationFn: deleteCoupon,
  });
  const { rewardful } = useRewardfulData();
  const affiliate = rewardful?.affiliate;
  const rewardfulCoupon = rewardful?.coupon;
  const rewardfulCouponId = rewardfulCoupon?.id || null;
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const couponParam = searchParams.get('coupon');
    if (couponParam) {
      setUrlCoupon(couponParam);
    }
  }, []);

  const { data: couponData, status: couponStatus } = useQuery({
    queryKey: ['COUPON_DATA', urlCoupon || rewardfulCouponId],
    queryFn: getCoupon,
    staleTime: Infinity,
    enabled: Boolean(urlCoupon) || Boolean(rewardfulCouponId),
  });
  const isCouponExpired = (coupon: CouponState | undefined): boolean => {
    return (
      coupon !== undefined &&
      coupon.redeemBy !== null &&
      coupon.redeemBy !== 0 &&
      differenceInMinutes(coupon.redeemBy, Date.now()) < 0
    );
  };
  const expiredCoupon = isCouponExpired(couponData);
  const { coupon: userCoupon } = userData.billing;
  const expiredUserCoupon = isCouponExpired(userCoupon);
  let coupon =
    couponData === undefined || expiredCoupon ? defaultCoupon : couponData;

  if (
    userCoupon.id !== '' &&
    !expiredUserCoupon &&
    (!urlCoupon || coupon.id === '' || expiredCoupon)
  )
    coupon = userCoupon;

  useEffect(() => {
    if (expiredCoupon) {
      addSnackbar({
        message: 'Coupon has expired',
        type: 'negative',
        actionLabel: 'OK',
      });
    } else if (
      couponData === undefined &&
      couponStatus === 'success' &&
      (urlCoupon !== null || urlCoupon === '')
    ) {
      addSnackbar({
        message: 'Coupon is invalid',
        type: 'warning',
        actionLabel: 'OK',
      });
    }
  }, [couponData, addSnackbar, expiredCoupon, urlCoupon, couponStatus]);
  //attach url coupon to user if user has no coupon attached and urlCoupon is not expired
  useEffect(() => {
    if (
      urlCoupon &&
      userData.loggedIn &&
      userCoupon.id === '' &&
      couponData !== undefined &&
      couponStatus === 'success' &&
      !expiredCoupon
    ) {
      updateUserCoupon(urlCoupon);
    }
    refetchUser();
  }, [
    userData.loggedIn,
    urlCoupon,
    userCoupon.id,
    expiredCoupon,
    couponData,
    couponStatus,
    refetchUser,
    updateUserCoupon,
  ]);

  //Attach url coupon to user If coupon is different from user's coupon and coupon is not expired and valid
  useEffect(() => {
    if (
      urlCoupon &&
      userCoupon.id !== '' &&
      urlCoupon !== userCoupon.id &&
      couponData !== undefined &&
      couponStatus === 'success' &&
      !expiredCoupon
    ) {
      updateUserCoupon(urlCoupon);
    }
  }, [
    urlCoupon,
    userCoupon.id,
    expiredCoupon,
    updateUserCoupon,
    couponData,
    couponStatus,
  ]);

  //Remove coupon from user if there's an expired coupon attached that was not redeemed
  useEffect(() => {
    if (expiredUserCoupon) {
      deleteUserCoupon();
    }
  }, [expiredUserCoupon, deleteUserCoupon]);

  useEffect(() => {
    if (status === 'success') {
      plans.forEach((plan) => {
        if (plan[userData.billing.plan])
          setUserPlan(plan[userData.billing.plan]);
        const planData = Object.values(plan)[0];
        if (planData.type === 'premium' || planData.type === 'unlimited') {
          const planInterval = planData.type + startCase(planData.interval);
          setPlanPrices((prevState) => ({
            ...prevState,
            [planInterval]: planData.amount,
          }));
        }
      });
      if (userData.billing.plan === '' && userData.loggedIn) refetchUser();
    }
  }, [plans, status, userData.billing.plan, refetchUser, userData.loggedIn]);

  useEffect(() => {
    // Non-Stripe subscribers can't access this page
    if (userData.loggedIn && userData.billing.provider !== 'stripe') {
      dispatch(redirect({ type: ROUTE_DASHBOARD }));
    }
  }, [userData.billing.provider, userData.loggedIn, dispatch]);

  const getDiscount = (plansPrices: PlansPrices): number => {
    return (
      ((plansPrices.premiumMonth - plansPrices.premiumYear / 12) /
        plansPrices.premiumMonth) *
      100
    );
  };

  const formatInterval = (interval: string): string => {
    return interval === 'year' ? 'Annual' : 'Monthly';
  };

  const daysUntilTrialEnds = useMemo(() => {
    if (typeof trialEnd === 'number') {
      return `${formatDistanceToNow(trialEnd)} left`;
    }
    return '';
  }, [trialEnd]);

  const upgradeText =
    userPlan.type === 'unlimited'
      ? 'Resume your subscription to keep access to paid features.'
      : 'Resume or upgrade your subscription to keep access to paid features.';

  const { isPastDue, planName, planInterval, stripePortalUrl } =
    useSubscriptionRecovery();
  const { trackEvent } = useTracking();
  return (
    <>
      {!hideUserMessage && (
        <MarkedTextContainer>
          <If
            conditions={{
              affiliateUser: !userData.loggedIn && Boolean(affiliate),
              trialUser: userData.billing.status === 'trialing',
              cancelUser: userData.billing.canceled,
              paidUser:
                userData.loggedIn &&
                userData.billing.status !== 'trialing' &&
                !isPastDue &&
                userPlan.type !== 'free',
              isPastDue: isPastDue,
            }}
          >
            {{
              isPastDue: () => (
                <MarkedText data-cy-id="past-due">
                  <Box marginBottom="x2spacePx" paddingTop="x2spacePx">
                    <Text color="almostBlack">
                      Your renewal payment for the{' '}
                      <Text as="span" color="spaceGray" typography="strong">
                        {planName} {planInterval} subscription
                      </Text>{' '}
                      recently failed. Your account is temporarily downgraded to
                      the free plan.
                    </Text>
                  </Box>
                  <Box marginBottom="x2spacePx">
                    <Text color="almostBlack">
                      Please{' '}
                      <Button
                        styleType="strongLink"
                        onClick={() => {
                          window.open(stripePortalUrl, '_blank');
                          trackEvent({
                            action: 'click',
                            modifier: 'updatePaymentDetails',
                          });
                        }}
                      >
                        update your payment method
                      </Button>{' '}
                      to successfully renew and enjoy your {planName}{' '}
                      {planInterval} subscription.
                    </Text>
                  </Box>
                </MarkedText>
              ),
              affiliateUser: (
                <MarkedText data-cy-id="marked-text-affiliate">
                  <span>Sign up now for an </span>
                  <strong>extended 14 day free Unlimited Trial</strong>
                  {Number(rewardfulCoupon?.percent_off) > 0 ? (
                    <span>
                      {' '}
                      and get a{' '}
                      <strong>
                        {rewardfulCoupon?.percent_off}% discount
                      </strong>{' '}
                      when you upgrade!
                    </span>
                  ) : null}
                </MarkedText>
              ),
              trialUser: (
                <MarkedText data-cy-id="marked-text">
                  <span>You have</span>
                  <strong> {daysUntilTrialEnds} </strong>
                  <span>{`on your ${userPlan.name} Trial. Upgrade now to `}</span>
                  {userData.billing.coupon.percentOff > 0 ? (
                    <span>
                      receive{' '}
                      <strong>
                        {userData.billing.coupon.percentOff * 100}% off!
                      </strong>
                    </span>
                  ) : (
                    <span>keep your access to the premium features!</span>
                  )}
                </MarkedText>
              ),
              paidUser: (
                <MarkedText data-cy-id="marked-text">
                  {`You're currently on the ${formatInterval(
                    userPlan.interval
                  )} ${userPlan.name} Plan.`}
                  {userPlan.interval === 'month' && (
                    <strong>
                      {` Upgrade to Annual ${
                        userPlan.name
                      } and save ${toTruncated(getDiscount(planPrices), 0)}%`}
                    </strong>
                  )}
                  {userPlan.type === 'premium' &&
                    userPlan.interval === 'year' && (
                      <strong>
                        {' '}
                        Upgrade to Unlimited for limitless access.
                      </strong>
                    )}
                </MarkedText>
              ),
              cancelUser: (
                <MarkedText data-cy-id="marked-text">
                  <span>{`Your ${userPlan.name} plan cancels in `}</span>
                  <strong>{`${formatDaysUntilEnd(
                    userData.billing.canceledAt
                  )}. `}</strong>
                  <span>{`${upgradeText}`}</span>
                </MarkedText>
              ),
            }}
          </If>
        </MarkedTextContainer>
      )}

      <PlansSelection
        darkBackground={darkBackground}
        hideFreePlan={hideFreePlan}
        userPlan={userPlan}
        coupon={coupon}
        onClose={onClose}
        onAction={onAction}
      />
      {!hideFreePlan && RUNTIME_ENV === 'native' && (
        <AddedFeatures darkBackground={darkBackground || false}>
          <FeatureItem notIncluded>
            <TickSolid />
            Tax deductible
          </FeatureItem>
          <FeatureItem notIncluded>
            <TickSolid />
            14 day money back guarantee
          </FeatureItem>
          <FeatureItem notIncluded>
            <TickSolid />
            Cancel at any time
          </FeatureItem>
          <FeatureItem notIncluded>
            <TickSolid />
            S&amp;P Global Data
          </FeatureItem>
        </AddedFeatures>
      )}
      {showModal}
    </>
  );
};
